/**
 * useDokumente — Hook für Dokumentenverwaltung
 * 
 * Funktionen:
 * - Ordner laden (Baumstruktur)
 * - Dokumente laden (nach Ordner)
 * - Datei-Upload mit Progress
 * - Dokument löschen (Soft-Delete)
 * - Ordner erstellen
 * - Tags laden/zuordnen
 * - Suche über alle Dokumente
 * - Verknüpfungen (Kontakte, Fälle, Aktenzeichen)
 * - Buchungsfunktion (Status, Betrag, Freigabe)
 * - Vorschau-URL (Signed URL für inline Anzeige)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

// ============================================================
// TYPES
// ============================================================

export interface DokumentOrdner {
  id: string;
  name: string;
  parent_id: string | null;
  pfad: string;
  icon: string | null;
  farbe: string | null;
  sortierung: number;
  ist_system: boolean;
  ist_privat: boolean;
  business_unit: string | null;
  created_at: string;
  children?: DokumentOrdner[];
  dokument_count?: number;
}

export interface DokumentDatei {
  id: string;
  akte_id: string | null;
  dateiname: string;
  dateipfad: string;
  dateigroesse: number | null;
  mime_type: string | null;
  kategorie: string | null;
  titel: string | null;
  beschreibung: string | null;
  version: number;
  vorgaenger_id: string | null;
  erstellt_am: string;
  erstellt_von: string | null;
  ordner_id: string | null;
  ist_favorit: boolean;
  updated_at: string;
  geloescht_am: string | null;
  // Neue Felder
  dokument_typ: string | null;
  buchungs_status: string | null;
  buchungs_datum: string | null;
  betrag: number | null;
  waehrung: string;
  freigabe_status: string | null;
  freigabe_von: string | null;
  freigabe_am: string | null;
  ki_analyse: Record<string, any> | null;
  notizen: string | null;
  tags: string[] | null;
}

export interface DokumentTag {
  id: string;
  name: string;
  farbe: string;
}

export interface DokumentVerknuepfung {
  id: string;
  dokument_id: string;
  verknuepfung_typ: string; // 'akte', 'kontakt', 'aufgabe', 'fall', 'fahrzeug'
  verknuepfung_id: string;
  created_at: string;
}

export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'done' | 'error';
  error?: string;
}

// Buchungs-Status Optionen
export const BUCHUNGS_STATUS = [
  { value: 'offen', label: 'Offen', color: '#F59E0B' },
  { value: 'geprueft', label: 'Geprüft', color: '#3B82F6' },
  { value: 'gebucht', label: 'Gebucht', color: '#10B981' },
  { value: 'bezahlt', label: 'Bezahlt', color: '#059669' },
  { value: 'storniert', label: 'Storniert', color: '#EF4444' },
] as const;

// Freigabe-Status Optionen
export const FREIGABE_STATUS = [
  { value: 'ausstehend', label: 'Ausstehend', color: '#F59E0B' },
  { value: 'freigegeben', label: 'Freigegeben', color: '#10B981' },
  { value: 'abgelehnt', label: 'Abgelehnt', color: '#EF4444' },
] as const;

// Dokument-Typ Optionen
export const DOKUMENT_TYPEN = [
  { value: 'rechnung', label: 'Rechnung', icon: 'receipt' },
  { value: 'beleg', label: 'Beleg / Quittung', icon: 'file-text' },
  { value: 'vertrag', label: 'Vertrag', icon: 'file-signature' },
  { value: 'gutachten', label: 'Gutachten', icon: 'file-check' },
  { value: 'korrespondenz', label: 'Korrespondenz', icon: 'mail' },
  { value: 'bescheid', label: 'Bescheid', icon: 'file-check' },
  { value: 'kontoauszug', label: 'Kontoauszug', icon: 'wallet' },
  { value: 'angebot', label: 'Angebot', icon: 'file-text' },
  { value: 'lieferschein', label: 'Lieferschein', icon: 'truck' },
  { value: 'mahnung', label: 'Mahnung', icon: 'alert-triangle' },
  { value: 'sonstiges', label: 'Sonstiges', icon: 'file' },
] as const;

// Verknüpfungs-Typ Optionen
export const VERKNUEPFUNG_TYPEN = [
  { value: 'akte', label: 'Akte / Aktenzeichen' },
  { value: 'kontakt', label: 'Kontakt / Kunde' },
  { value: 'aufgabe', label: 'Aufgabe' },
  { value: 'fall', label: 'Fall' },
  { value: 'fahrzeug', label: 'Fahrzeug' },
  { value: 'immobilie', label: 'Immobilie' },
] as const;

// ============================================================
// ORDNER HOOK
// ============================================================

export function useOrdner() {
  const [ordner, setOrdner] = useState<DokumentOrdner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadOrdner = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Paginiert laden — Supabase hat ein Standard-Limit von 1000 Zeilen
      const PAGE_SIZE = 1000;
      let allData: any[] = [];
      let page = 0;
      let hasMore = true;
      while (hasMore) {
        const from = page * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        const { data, error: err } = await supabase
          .from('dokument_ordner')
          .select('*')
          .order('sortierung', { ascending: true })
          .range(from, to);
        if (err) throw err;
        const chunk = data || [];
        allData = allData.concat(chunk);
        hasMore = chunk.length === PAGE_SIZE;
        page++;
      }

      // Baumstruktur aufbauen
      const flat = allData as DokumentOrdner[];
      const map = new Map<string, DokumentOrdner>();
      flat.forEach(o => map.set(o.id, { ...o, children: [] }));

      const roots: DokumentOrdner[] = [];
      flat.forEach(o => {
        const node = map.get(o.id)!;
        if (o.parent_id && map.has(o.parent_id)) {
          map.get(o.parent_id)!.children!.push(node);
        } else if (!o.parent_id) {
          roots.push(node);
        }
      });

      // Sortierung innerhalb der Kinder
      const sortChildren = (nodes: DokumentOrdner[]) => {
        nodes.sort((a, b) => a.sortierung - b.sortierung);
        nodes.forEach(n => n.children && sortChildren(n.children));
      };
      sortChildren(roots);

      setOrdner(roots);
    } catch (e: any) {
      console.error('Ordner laden fehlgeschlagen:', e);
      setError(e.message || 'Fehler beim Laden der Ordner');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrdner(); }, [loadOrdner]);

  const createOrdner = useCallback(async (name: string, parentId: string | null, icon?: string) => {
    // Finde parent Ordner rekursiv
    const findOrdner = (nodes: DokumentOrdner[], id: string): DokumentOrdner | null => {
      for (const n of nodes) {
        if (n.id === id) return n;
        if (n.children) {
          const found = findOrdner(n.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const parentOrdner = parentId ? findOrdner(ordner, parentId) : null;
    const parentPfad = parentOrdner?.pfad || '';
    const slug = name.toLowerCase().replace(/[^a-z0-9äöüß]/g, '-').replace(/-+/g, '-');
    const pfad = `${parentPfad}/${slug}`;

    const { data, error: err } = await supabase
      .from('dokument_ordner')
      .insert({
        name,
        parent_id: parentId,
        pfad,
        icon: icon || 'folder',
        sortierung: 99,
        ist_system: false,
        ist_privat: false,
      })
      .select()
      .single();

    if (err) throw err;
    await loadOrdner();
    return data;
  }, [ordner, loadOrdner]);

  const renameOrdner = useCallback(async (id: string, newName: string) => {
    const { error: err } = await supabase
      .from('dokument_ordner')
      .update({ name: newName })
      .eq('id', id);
    if (err) throw err;
    await loadOrdner();
  }, [loadOrdner]);

  const moveOrdner = useCallback(async (id: string, newParentId: string | null) => {
    const { error: err } = await supabase
      .from('dokument_ordner')
      .update({ parent_id: newParentId })
      .eq('id', id);
    if (err) throw err;
    await loadOrdner();
  }, [loadOrdner]);

  const deleteOrdner = useCallback(async (id: string, bestaetigungsName?: string) => {
    // ── ROBUSTER LÖSCHSCHUTZ ──
    // Schritt 1: Ordner-Daten laden
    const { data: ordnerData } = await supabase
      .from('dokument_ordner')
      .select('id, name, ist_system, pfad')
      .eq('id', id)
      .single();

    if (!ordnerData) {
      throw new Error('Ordner nicht gefunden.');
    }

    // Schritt 2: System-Ordner NIEMALS löschbar
    if (ordnerData.ist_system) {
      throw new Error('System-Ordner können nicht gelöscht werden. Diese Ordner sind für die Struktur des Systems erforderlich.');
    }

    // Schritt 3: Bestätigungsname muss exakt übereinstimmen
    if (!bestaetigungsName || bestaetigungsName.trim() !== ordnerData.name.trim()) {
      throw new Error(`SICHERHEITSCHECK: Bitte geben Sie den Ordnernamen "${ordnerData.name}" exakt ein, um das Löschen zu bestätigen.`);
    }

    // Schritt 4: Prüfe ob Ordner Unterordner hat
    const { data: children } = await supabase
      .from('dokument_ordner')
      .select('id')
      .eq('parent_id', id);
    if (children && children.length > 0) {
      throw new Error(`Ordner "${ordnerData.name}" enthält ${children.length} Unterordner. Bitte zuerst alle Unterordner verschieben oder einzeln löschen.`);
    }

    // Schritt 5: Prüfe ob Ordner Dokumente hat (auch gelöschte)
    const { data: docs } = await supabase
      .from('dokumente')
      .select('id')
      .eq('ordner_id', id)
      .is('geloescht_am', null)
      .limit(1);
    if (docs && docs.length > 0) {
      throw new Error(`Ordner "${ordnerData.name}" enthält noch Dokumente. Bitte zuerst alle Dokumente verschieben oder löschen.`);
    }

    // Schritt 6: Soft-Delete statt Hard-Delete — Ordner wird archiviert
    // Wir setzen ein geloescht_am Feld (falls vorhanden) oder löschen nur leere, nicht-system Ordner
    const { error: err } = await supabase
      .from('dokument_ordner')
      .delete()
      .eq('id', id)
      .eq('ist_system', false); // Doppelte Absicherung auf DB-Ebene
    if (err) throw err;
    await loadOrdner();
  }, [loadOrdner]);

  return { ordner, loading, error, refetch: loadOrdner, createOrdner, renameOrdner, moveOrdner, deleteOrdner };
}

// ============================================================
// DOKUMENTE HOOK
// ============================================================

export function useDokumente(ordnerId: string | null) {
  const [dokumente, setDokumente] = useState<DokumentDatei[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDokumente = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('dokumente')
        .select('*')
        .is('geloescht_am', null)
        .order('erstellt_am', { ascending: false });

      if (ordnerId) {
        query = query.eq('ordner_id', ordnerId);
      }

      const { data, error: err } = await query;
      if (err) throw err;
      setDokumente((data || []) as DokumentDatei[]);
    } catch (e: any) {
      console.error('Dokumente laden fehlgeschlagen:', e);
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [ordnerId]);

  useEffect(() => { loadDokumente(); }, [loadDokumente]);

  return { dokumente, loading, error, refetch: loadDokumente };
}

// ============================================================
// UPLOAD HOOK
// ============================================================

// Supabase Storage lehnt Keys mit Nicht-ASCII-Zeichen (ä, ö, ü, ß, …) mit HTTP 400 ab.
// Wir behalten den Original-Dateinamen in der DB-Spalte `dateiname`, sanitizen ihn aber
// für den Storage-Pfad.
function sanitizeStorageFilename(name: string): string {
  return name
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
    // Weitere Diakritika (é, è, ñ, …) in Basisbuchstaben zerlegen
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    // Alle übrigen Nicht-ASCII-Zeichen durch '_' ersetzen.
    // Erlaubte S3-Key-Zeichen: Buchstaben/Ziffern/_ sowie / ! - . * ' ( ) space & $ @ = ; : + , ?
    .replace(/[^\w/!.*'()\-\s&$@=;:+,?]/g, '_');
}

export function useUpload() {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);

  const uploadDatei = useCallback(async (
    file: File,
    ordnerId: string | null,
    userId?: string | null,
    akteId?: string | null,
  ): Promise<DokumentDatei | null> => {
    const fileName = file.name;
    const safeName = sanitizeStorageFilename(fileName);
    const timestamp = Date.now();
    const storagePath = ordnerId
      ? `${ordnerId}/${timestamp}_${safeName}`
      : `eingangskorb/${timestamp}_${safeName}`;

    setUploads(prev => [...prev, { fileName, progress: 0, status: 'uploading' }]);

    try {
      // 1. Upload to Supabase Storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from('dokumente')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (storageError) throw storageError;

      setUploads(prev => prev.map(u =>
        u.fileName === fileName ? { ...u, progress: 70 } : u
      ));

      // Dokumenttyp aus MIME-Type ableiten
      const detectedType = detectDokumentTyp(file.type, fileName);

      // 2. Create database record
      const { data: dbData, error: dbError } = await supabase
        .from('dokumente')
        .insert({
          dateiname: fileName,
          dateipfad: storageData.path,
          dateigroesse: file.size,
          mime_type: file.type || 'application/octet-stream',
          kategorie: detectedType || 'sonstiges',
          dokument_typ: detectedType,
          ordner_id: ordnerId,
          erstellt_von: userId || null,
          akte_id: akteId || null,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      setUploads(prev => prev.map(u =>
        u.fileName === fileName ? { ...u, progress: 100, status: 'done' } : u
      ));

      return dbData as DokumentDatei;
    } catch (e: any) {
      console.error('Upload fehlgeschlagen:', e);
      setUploads(prev => prev.map(u =>
        u.fileName === fileName ? { ...u, status: 'error', error: e.message } : u
      ));
      return null;
    }
  }, []);

  const uploadMultiple = useCallback(async (
    files: File[],
    ordnerId: string | null,
    userId?: string | null,
  ): Promise<DokumentDatei[]> => {
    const results: DokumentDatei[] = [];
    for (const file of files) {
      const result = await uploadDatei(file, ordnerId, userId);
      if (result) results.push(result);
    }
    return results;
  }, [uploadDatei]);

  const clearUploads = useCallback(() => {
    setUploads([]);
  }, []);

  return { uploads, uploadDatei, uploadMultiple, clearUploads };
}

// ============================================================
// TAGS HOOK
// ============================================================

export function useTags() {
  const [tags, setTags] = useState<DokumentTag[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('dokument_tags')
        .select('*')
        .order('name');
      setTags((data || []) as DokumentTag[]);
      setLoading(false);
    })();
  }, []);

  return { tags, loading };
}

// ============================================================
// SUCHE HOOK
// ============================================================

export interface SuchErgebnis extends DokumentDatei {
  ordner_name?: string;
  ordner_pfad?: string;
  treffer_feld?: string; // welches Feld den Treffer enthält
}

export interface OrdnerSuchErgebnis {
  id: string;
  name: string;
  pfad: string;
  icon: string | null;
  parent_id: string | null;
}

export function useDokumentSuche() {
  const [results, setResults] = useState<SuchErgebnis[]>([]);
  const [ordnerResults, setOrdnerResults] = useState<OrdnerSuchErgebnis[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const search = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      setOrdnerResults([]);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    try {
      // 1. Dokumente durchsuchen — erweiterte Felder
      const q = query.trim();
      const { data: dokData, count } = await supabase
        .from('dokumente')
        .select('*', { count: 'exact' })
        .is('geloescht_am', null)
        .or(`dateiname.ilike.%${q}%,titel.ilike.%${q}%,beschreibung.ilike.%${q}%,notizen.ilike.%${q}%,dokument_typ.ilike.%${q}%`)
        .order('erstellt_am', { ascending: false })
        .limit(100);

      // 2. Ordner-Pfade für die Ergebnisse laden
      const ordnerIds = [...new Set((dokData || []).map((d: any) => d.ordner_id).filter(Boolean))];
      let ordnerMap: Record<string, { name: string; pfad: string }> = {};
      if (ordnerIds.length > 0) {
        const { data: ordnerData } = await supabase
          .from('dokument_ordner')
          .select('id, name, pfad')
          .in('id', ordnerIds);
        if (ordnerData) {
          ordnerData.forEach((o: any) => { ordnerMap[o.id] = { name: o.name, pfad: o.pfad }; });
        }
      }

      // 3. Treffer-Feld bestimmen und Ordner-Info anhängen
      const enriched: SuchErgebnis[] = (dokData || []).map((d: any) => {
        let treffer_feld = 'dateiname';
        const lq = q.toLowerCase();
        if (d.titel?.toLowerCase().includes(lq)) treffer_feld = 'titel';
        else if (d.beschreibung?.toLowerCase().includes(lq)) treffer_feld = 'beschreibung';
        else if (d.notizen?.toLowerCase().includes(lq)) treffer_feld = 'notizen';
        else if (d.dokument_typ?.toLowerCase().includes(lq)) treffer_feld = 'dokument_typ';

        const ordnerInfo = d.ordner_id ? ordnerMap[d.ordner_id] : null;
        return {
          ...d,
          ordner_name: ordnerInfo?.name || undefined,
          ordner_pfad: ordnerInfo?.pfad || undefined,
          treffer_feld,
        } as SuchErgebnis;
      });

      setResults(enriched);
      setTotalCount(count || enriched.length);

      // 4. Ordner-Suche — auch Ordnernamen durchsuchen
      const { data: ordnerSuchData } = await supabase
        .from('dokument_ordner')
        .select('id, name, pfad, icon, parent_id')
        .ilike('name', `%${q}%`)
        .order('name', { ascending: true })
        .limit(20);
      setOrdnerResults((ordnerSuchData || []) as OrdnerSuchErgebnis[]);

    } catch (e) {
      console.error('Suche fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearResults = useCallback(() => {
    setResults([]);
    setOrdnerResults([]);
    setTotalCount(0);
  }, []);

  return { results, ordnerResults, loading, totalCount, search, clearResults };
}

// ============================================================
// VERKNÜPFUNGEN HOOK
// ============================================================

export function useVerknuepfungen(dokumentId: string | null) {
  const [verknuepfungen, setVerknuepfungen] = useState<DokumentVerknuepfung[]>([]);
  const [loading, setLoading] = useState(false);

  const loadVerknuepfungen = useCallback(async () => {
    if (!dokumentId) { setVerknuepfungen([]); return; }
    setLoading(true);
    try {
      const { data } = await supabase
        .from('dokument_verknuepfungen')
        .select('*')
        .eq('dokument_id', dokumentId)
        .order('created_at', { ascending: false });
      setVerknuepfungen((data || []) as DokumentVerknuepfung[]);
    } catch (e) {
      console.error('Verknüpfungen laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, [dokumentId]);

  useEffect(() => { loadVerknuepfungen(); }, [loadVerknuepfungen]);

  const addVerknuepfung = useCallback(async (typ: string, id: string) => {
    if (!dokumentId) return;
    const { error } = await supabase
      .from('dokument_verknuepfungen')
      .insert({ dokument_id: dokumentId, verknuepfung_typ: typ, verknuepfung_id: id });
    if (error) throw error;
    await loadVerknuepfungen();
  }, [dokumentId, loadVerknuepfungen]);

  const removeVerknuepfung = useCallback(async (id: string) => {
    const { error } = await supabase
      .from('dokument_verknuepfungen')
      .delete()
      .eq('id', id);
    if (error) throw error;
    await loadVerknuepfungen();
  }, [loadVerknuepfungen]);

  return { verknuepfungen, loading, addVerknuepfung, removeVerknuepfung, refetch: loadVerknuepfungen };
}

// ============================================================
// DOKUMENT AKTIONEN
// ============================================================

export function useDokumentAktionen() {
  const deleteDokument = useCallback(async (id: string) => {
    // Soft-Delete
    const { error } = await supabase
      .from('dokumente')
      .update({ geloescht_am: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const moveDokument = useCallback(async (id: string, ordnerId: string | null) => {
    const { error } = await supabase
      .from('dokumente')
      .update({ ordner_id: ordnerId, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const toggleFavorit = useCallback(async (id: string, current: boolean) => {
    const { error } = await supabase
      .from('dokumente')
      .update({ ist_favorit: !current, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const renameDokument = useCallback(async (id: string, titel: string) => {
    const { error } = await supabase
      .from('dokumente')
      .update({ titel, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const updateDokumentTyp = useCallback(async (id: string, typ: string) => {
    const { error } = await supabase
      .from('dokumente')
      .update({ dokument_typ: typ, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const updateBuchung = useCallback(async (id: string, updates: {
    buchungs_status?: string;
    buchungs_datum?: string;
    betrag?: number;
    waehrung?: string;
  }) => {
    const { error } = await supabase
      .from('dokumente')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const updateFreigabe = useCallback(async (id: string, status: string, userId?: string) => {
    const updates: any = {
      freigabe_status: status,
      updated_at: new Date().toISOString(),
    };
    if (status === 'freigegeben' || status === 'abgelehnt') {
      updates.freigabe_von = userId;
      updates.freigabe_am = new Date().toISOString();
    }
    const { error } = await supabase
      .from('dokumente')
      .update(updates)
      .eq('id', id);
    if (error) throw error;
  }, []);

  const updateNotizen = useCallback(async (id: string, notizen: string) => {
    const { error } = await supabase
      .from('dokumente')
      .update({ notizen, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const updateDokumentTags = useCallback(async (id: string, tags: string[]) => {
    const normalized = Array.from(new Set(tags.map(t => t.trim()).filter(Boolean)));
    const { error } = await supabase
      .from('dokumente')
      .update({ tags: normalized, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
  }, []);

  const getDownloadUrl = useCallback(async (dateipfad: string) => {
    const { data, error } = await supabase.storage
      .from('dokumente')
      .createSignedUrl(dateipfad, 3600); // 1 hour
    if (error) throw error;
    return data.signedUrl;
  }, []);

  const getPreviewUrl = useCallback(async (dateipfad: string) => {
    // Signed URL für inline Vorschau (längere Gültigkeit)
    const { data, error } = await supabase.storage
      .from('dokumente')
      .createSignedUrl(dateipfad, 7200); // 2 hours
    if (error) throw error;
    return data.signedUrl;
  }, []);

  return {
    deleteDokument, moveDokument, toggleFavorit, renameDokument,
    updateDokumentTyp, updateBuchung, updateFreigabe, updateNotizen,
    updateDokumentTags,
    getDownloadUrl, getPreviewUrl,
  };
}

// ============================================================
// HELPER: Dokumenttyp aus MIME/Dateiname erkennen
// ============================================================

function detectDokumentTyp(mimeType: string, fileName: string): string | null {
  const lower = fileName.toLowerCase();
  
  // Dateiname-basierte Erkennung
  if (lower.includes('rechnung') || lower.includes('invoice')) return 'rechnung';
  if (lower.includes('beleg') || lower.includes('quittung') || lower.includes('receipt')) return 'beleg';
  if (lower.includes('vertrag') || lower.includes('contract')) return 'vertrag';
  if (lower.includes('gutachten') || lower.includes('expertise')) return 'gutachten';
  if (lower.includes('kontoauszug') || lower.includes('statement')) return 'kontoauszug';
  if (lower.includes('angebot') || lower.includes('offer') || lower.includes('quote')) return 'angebot';
  if (lower.includes('lieferschein') || lower.includes('delivery')) return 'lieferschein';
  if (lower.includes('mahnung') || lower.includes('reminder')) return 'mahnung';
  if (lower.includes('bescheid') || lower.includes('notice')) return 'bescheid';
  
  return null;
}

// ============================================================
// HELPER: Kann Datei inline angezeigt werden?
// ============================================================

export function canPreviewInline(mimeType: string | null): boolean {
  if (!mimeType) return false;
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    mimeType === 'text/plain' ||
    mimeType === 'text/html' ||
    mimeType === 'text/csv'
  );
}

export function getPreviewType(mimeType: string | null): 'pdf' | 'image' | 'text' | 'none' {
  if (!mimeType) return 'none';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('text/')) return 'text';
  return 'none';
}

// ============================================================
// PFLICHTDOKUMENTE
// ============================================================

export interface PflichtDokument {
  id: string;
  ordner_id: string;
  name: string;
  beschreibung: string | null;
  ist_pflicht: boolean;
  sortierung: number;
  dokument_id: string | null;
  status: 'fehlt' | 'vorhanden' | 'abgelaufen';
  gueltig_bis: string | null;
  created_at: string;
  updated_at: string;
}

export function usePflichtdokumente(ordnerId: string | null) {
  const [pflichtdokumente, setPflichtdokumente] = useState<PflichtDokument[]>([]);
  const [loading, setLoading] = useState(false);

  const loadPflichtdokumente = useCallback(async () => {
    if (!ordnerId) {
      setPflichtdokumente([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('pflichtdokument_vorlagen')
        .select('*')
        .eq('ordner_id', ordnerId)
        .order('sortierung', { ascending: true });

      if (error) throw error;
      setPflichtdokumente((data || []) as PflichtDokument[]);
    } catch (e: any) {
      console.error('Pflichtdokumente laden fehlgeschlagen:', e);
    } finally {
      setLoading(false);
    }
  }, [ordnerId]);

  useEffect(() => { loadPflichtdokumente(); }, [loadPflichtdokumente]);

  const linkDokument = useCallback(async (pflichtId: string, dokumentId: string) => {
    const { error } = await supabase
      .from('pflichtdokument_vorlagen')
      .update({ dokument_id: dokumentId, status: 'vorhanden', updated_at: new Date().toISOString() })
      .eq('id', pflichtId);
    if (error) throw error;
    await loadPflichtdokumente();
  }, [loadPflichtdokumente]);

  const unlinkDokument = useCallback(async (pflichtId: string) => {
    const { error } = await supabase
      .from('pflichtdokument_vorlagen')
      .update({ dokument_id: null, status: 'fehlt', updated_at: new Date().toISOString() })
      .eq('id', pflichtId);
    if (error) throw error;
    await loadPflichtdokumente();
  }, [loadPflichtdokumente]);

  const stats = {
    total: pflichtdokumente.length,
    pflicht: pflichtdokumente.filter(p => p.ist_pflicht).length,
    vorhanden: pflichtdokumente.filter(p => p.status === 'vorhanden').length,
    fehlt: pflichtdokumente.filter(p => p.status === 'fehlt' && p.ist_pflicht).length,
    abgelaufen: pflichtdokumente.filter(p => p.status === 'abgelaufen').length,
    prozent: pflichtdokumente.filter(p => p.ist_pflicht).length > 0
      ? Math.round((pflichtdokumente.filter(p => p.status === 'vorhanden' && p.ist_pflicht).length / pflichtdokumente.filter(p => p.ist_pflicht).length) * 100)
      : 100,
  };

  return { pflichtdokumente, loading, stats, refetch: loadPflichtdokumente, linkDokument, unlinkDokument };
}
